import Axios, { AxiosRequestConfig } from 'axios';

import { signIn, signOut } from '@/redux/auth/auth.slice';
import { disconnect } from '@/redux/notification/notification.slice';
import type { Store } from '@/redux/store';
import { getAuthRefreshDeadline } from './authToken';
import { getPblAssistanceToken } from './pblAssistance';

declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean;
    skipAuthRefresh?: boolean;
    _authContext?: { actor: string | null; generation: number };
  }
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
}

let refreshPromise: Promise<string> | null = null;
let refreshContext: { actor: string | null; generation: number; token: string } | null = null;
let interceptorsRegistered = false;
let authStorageListener: ((event: StorageEvent) => void) | null = null;
let unsubscribeAuthState: (() => void) | null = null;

const readPersistedAuth = (): { isLogin: boolean; userId?: number; token?: string; deadline?: number } | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem('user');
    if (!value) return null;
    const persisted = JSON.parse(value);
    const user = JSON.parse(persisted.userData || '{}');
    return {
      isLogin: JSON.parse(persisted.isLogin || 'false') === true,
      userId: Number(user.id),
      token: JSON.parse(persisted.accessToken || 'null') || undefined,
      deadline: Number(JSON.parse(persisted.refreshTokenTime || 'null')) || undefined,
    };
  } catch {
    return null;
  }
};

export const getAuthDeviceId = (): string | null => {
  if (typeof window === 'undefined' || !window.sessionStorage || !window.crypto?.getRandomValues) return null;
  const storageKey = 'external.authDeviceId';
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing && /^[A-Za-z0-9_-]{20,128}$/.test(existing)) return existing;

  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  const generated = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  window.sessionStorage.setItem(storageKey, generated);
  return generated;
};

const isTokenExpiringSoon = (refreshTokenTime: number | undefined): boolean => {
  if (!refreshTokenTime) return true;
  return refreshTokenTime - Date.now() <= 5 * 60 * 1000;
};

export const axios = Axios.create();
// Public share credentials are independent of the signed-in member session.
const isPublicShareRequest = (url?: string) => typeof url === 'string' && /^\/api\/public\/share(?:\/|$)/.test(url);

export const axiosInterceptor = (store: Store, options: { persistAuth?: () => Promise<unknown> } = {}) => {
  if (interceptorsRegistered) {
    return;
  }

  interceptorsRegistered = true;

  type AuthContext = { actor: string | null; generation: number };
  type AuthCheckpoint = { context: AuthContext; token: string; promise: Promise<unknown> | null };
  let pendingCheckpoint: AuthCheckpoint | null = null;
  const actor = () => {
    const auth = store.getState().auth;
    return auth.isLogin && auth.userData?.id ? String(auth.userData.id) : null;
  };
  let generation = 0;
  let previousActor = actor();
  let previousToken = store.getState().auth.accessToken;
  let credentialWrite: { actor: string | null; token?: string } | null = null;
  if (typeof store.subscribe === 'function') unsubscribeAuthState = store.subscribe(() => {
    const nextActor = actor();
    const nextToken = store.getState().auth.accessToken;
    const published = credentialWrite?.actor === nextActor && credentialWrite?.token === nextToken;
    if (nextActor !== previousActor || (nextToken !== previousToken && !published)) generation++;
    previousActor = nextActor;
    previousToken = nextToken;
  });
  const context = (): AuthContext => ({ actor: actor(), generation });
  const isCurrent = (expected?: AuthContext) => !expected || (expected.actor === actor() && expected.generation === generation);
  const changedContext = () => Object.assign(new Error('Authentication changed before the request completed.'), { code: 'AUTH_CONTEXT_CHANGED' });
  const publishAuth = (payload: Parameters<typeof signIn>[0]) => {
    const previous = credentialWrite;
    credentialWrite = { actor: payload.isLogin && payload.userData?.id ? String(payload.userData.id) : null, token: payload.accessToken };
    try { store.dispatch(signIn(payload)); } finally { credentialWrite = previous; }
  };
  const retiredTokens = new Set<string>();
  const retireToken = (token: string) => {
    retiredTokens.add(token);
    if (retiredTokens.size > 64) retiredTokens.delete(retiredTokens.values().next().value as string);
  };
  const completeCheckpoint = (entry: AuthCheckpoint): Promise<unknown> => {
    if (!entry.promise) entry.promise = Promise.resolve().then(async () => {
      if (!isCurrent(entry.context) || store.getState().auth.accessToken !== entry.token) throw changedContext();
      if (options.persistAuth) {
        // 真實 redux-persist 可能吞掉 setItem 失敗，且失敗後單純 flush 不會重新排入相同狀態。
        const auth = store.getState().auth;
        publishAuth({ ...auth, userData: { ...auth.userData } });
        await options.persistAuth();
        if (!isCurrent(entry.context) || store.getState().auth.accessToken !== entry.token) throw changedContext();
        const saved = readPersistedAuth();
        if (!saved?.isLogin || saved.userId !== Number(entry.context.actor) || saved.token !== entry.token
          || saved.deadline !== store.getState().auth.refreshTokenTime) {
          throw Object.assign(new Error('Authentication could not be saved. Retry before continuing.'), { code: 'AUTH_PERSISTENCE_FAILED' });
        }
      }
      if (!isCurrent(entry.context) || store.getState().auth.accessToken !== entry.token) throw changedContext();
      if (pendingCheckpoint === entry) pendingCheckpoint = null;
    }).finally(() => { entry.promise = null; });
    return entry.promise;
  };

  const handleAuthError = (expected?: AuthContext) => {
    if (!isCurrent(expected)) return;
    store.dispatch(signOut());
    store.dispatch(disconnect());

    const currentPath = window.location.pathname;

    if (currentPath !== '/auth/signin') {
      const redirectUrl = `/auth/signin?path=${encodeURIComponent(currentPath)}`;
      console.log('Redirect to sign in:', redirectUrl);
      // window.location.href = redirectUrl;
    }
  };

  const adoptPeerToken = (oldToken: string): string | null => {
    const current = store.getState().auth;
    const persisted = readPersistedAuth();
    if (!persisted?.isLogin || !persisted.token || persisted.token === oldToken
      || persisted.userId !== Number(current.userData?.id)
      || retiredTokens.has(persisted.token)
      || !persisted.deadline || persisted.deadline < (current.refreshTokenTime || 0)) return null;

    retireToken(oldToken);
    publishAuth({
      ...current,
      accessToken: persisted.token,
      refreshTokenTime: persisted.deadline,
    });
    return persisted.token;
  };

  const waitForPeerToken = (oldToken: string, expected: AuthContext): Promise<string | null> => new Promise((resolve) => {
    if (!isCurrent(expected)) { resolve(null); return; }
    const current = adoptPeerToken(oldToken);
    if (current) { resolve(current); return; }
    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'user') return;
      const token = isCurrent(expected) ? adoptPeerToken(oldToken) : null;
      if (token) finish(token);
    };
    const finish = (token: string | null) => {
      window.clearTimeout(timeout);
      window.removeEventListener('storage', onStorage);
      resolve(token);
    };
    const timeout = window.setTimeout(() => finish(isCurrent(expected) ? adoptPeerToken(oldToken) : null), 1500);
    window.addEventListener('storage', onStorage);
  });

  authStorageListener = (event: StorageEvent) => {
    if (event.key !== 'user') return;
    const state = store.getState().auth;
    if (state.isLogin && state.accessToken) {
      adoptPeerToken(state.accessToken);
    }
  };
  window.addEventListener('storage', authStorageListener);

  const refreshAccessToken = async (token: string, expected: AuthContext): Promise<string> => {
    if (!expected.actor || !isCurrent(expected)) throw changedContext();
    const activeToken = store.getState().auth.accessToken;
    if (pendingCheckpoint && isCurrent(pendingCheckpoint.context) && pendingCheckpoint.token === activeToken) {
      await completeCheckpoint(pendingCheckpoint);
      if (!isCurrent(expected)) throw changedContext();
      return store.getState().auth.accessToken || Promise.reject(changedContext());
    }
    if (activeToken && activeToken !== token) return activeToken;
    const peerToken = adoptPeerToken(token);
    if (peerToken) return peerToken;
    if (!refreshPromise || refreshContext?.actor !== expected.actor || refreshContext?.generation !== expected.generation || refreshContext?.token !== token) {
      const attempt = { ...expected, token };
      refreshContext = attempt;
      const pending = axios
        .post('/api/auth/refresh', token, {
          skipAuthRefresh: true,
          _authContext: expected,
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(async (response) => {
          if (!isCurrent(expected)) throw changedContext();
          const state = store.getState();
          if (state.auth.accessToken !== token) return state.auth.accessToken || Promise.reject(changedContext());
          const newToken = response.data.data.access_token;
          const refreshedUser = response.data.data.user;
          const refreshedApplications = response.data.data.applications;
          if (typeof newToken !== 'string' || !newToken || (refreshedUser && String(refreshedUser.id) !== expected.actor)) throw changedContext();

          retireToken(token);

          publishAuth({
              ...state.auth,
              isLogin: true,
              accessToken: newToken,
              userData: refreshedUser || state.auth.userData,
              applications: refreshedApplications || state.auth.applications,
              refreshTokenTime: getAuthRefreshDeadline(response.data.data.expires_in),
            });
          // Redux 更新不等於 redux-persist 已落盤；原請求只能在持久 checkpoint 完成後放行。
          const checkpoint: AuthCheckpoint = { context: expected, token: newToken, promise: null };
          pendingCheckpoint = checkpoint;
          await completeCheckpoint(checkpoint);
          return newToken;
        })
        .catch(async (error) => {
          if (!isCurrent(expected)) throw changedContext();
          if (error.response?.status === 401) {
            const peerToken = await waitForPeerToken(token, expected);
            if (peerToken) return peerToken;
          }
          throw error;
        })
        .finally(() => {
          if (refreshContext === attempt) { refreshPromise = null; refreshContext = null; }
        });
      refreshPromise = pending;
    }

    return refreshPromise;
  };

  const requestBefore = async (config: CustomAxiosRequestConfig): Promise<CustomAxiosRequestConfig> => {
    config.headers = config.headers || {};
    if (isPublicShareRequest(config.url)) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
      return config;
    }
    config._authContext = config._authContext || context();
    if (!isCurrent(config._authContext)) throw changedContext();
    if (config.skipAuthRefresh) {
      return config;
    }
    config.headers = config.headers || {};
    const authDeviceId = getAuthDeviceId();
    if (authDeviceId) config.headers['X-Auth-Device'] = authDeviceId;
    const assistanceToken = getPblAssistanceToken();
    if (assistanceToken && typeof config.url === 'string' && config.url.startsWith('/api/pbl/')) {
      config.headers['X-PBL-Assistance-Session'] = assistanceToken;
    }

    const state = store.getState();
    const { isLogin, userData, accessToken, refreshTokenTime } = state.auth;

    if (isLogin && userData && accessToken) {
      if (pendingCheckpoint) {
        if (!isCurrent(pendingCheckpoint.context) || pendingCheckpoint.token !== accessToken) pendingCheckpoint = null;
        else await completeCheckpoint(pendingCheckpoint);
      }
      if (!isCurrent(config._authContext)) throw changedContext();
      const currentToken = adoptPeerToken(accessToken) || accessToken;
      if (currentToken !== accessToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
        return config;
      }
      if (isTokenExpiringSoon(refreshTokenTime)) {
        try {
          const newToken = await refreshAccessToken(accessToken, config._authContext);

          config.headers.Authorization = `Bearer ${newToken}`;
          return config;
        } catch (error: any) {
          if (error.response?.status === 401) {
            handleAuthError(config._authContext);
          }

          return Promise.reject(error);
        }
      }

      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  };

  const responseError = async (error: any): Promise<any> => {
    const originRequest = error.config || {};
    if (isPublicShareRequest(originRequest.url)) return Promise.reject(error);
    if (!isCurrent(originRequest._authContext)) return Promise.reject(changedContext());

    if (originRequest._retry || originRequest.skipAuthRefresh) {
      if (error.response?.status === 401 && originRequest.url !== '/api/auth/refresh') {
        handleAuthError(originRequest._authContext);
      }

      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      originRequest._retry = true;
      const state = store.getState();
      const { accessToken } = state.auth;

      if (!accessToken) {
        handleAuthError(originRequest._authContext);
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshAccessToken(accessToken, originRequest._authContext || context());

        originRequest.headers = originRequest.headers || {};
        originRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originRequest);
      } catch (refreshError: any) {
        if (refreshError.response?.status === 401) {
          handleAuthError(originRequest._authContext);
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };

  axios.interceptors.request.use(requestBefore);
  axios.interceptors.response.use(undefined, responseError);
};

export const resetAxiosInterceptorForTests = () => {
  if (authStorageListener && typeof window !== 'undefined') {
    window.removeEventListener('storage', authStorageListener);
  }
  authStorageListener = null;
  unsubscribeAuthState?.(); unsubscribeAuthState = null;
  refreshPromise = null;
  refreshContext = null;
  interceptorsRegistered = false;
};

export async function axiosFetch<T>(axiosConfig: AxiosConfig) {
  const response: AxiosResponse<T> = {
    res: {
      success: false,
      message: '',
      data: {},
    },
  };

  const { axiosInstance, method, url, requestConfig = {} } = axiosConfig;
  try {
    const res = await axiosInstance[method.toLowerCase()](url, requestConfig.data);
    response.res = res.data;
  } catch (err: any) {
    response.res = err.response.data;
  } finally {
    return response;
  }
}
